import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { siteConfig } from '@/constants/site'
import { primaryNav } from '@/constants/navigation'
import { Logo } from '@/components/generals/logo'
import { Button } from '@/components/generals/button'

const join = (...values: (string | false | undefined)[]): string =>
    values.filter(Boolean).join(' ')

/** Matches the `medium` collapse breakpoint the original navigation used. */
const COLLAPSE_WIDTH = 991

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)

    const isCurrent = (href: string): boolean =>
        href === '/' ? router.pathname === '/' : router.pathname.startsWith(href)

    useEffect(() => {
        const close = (): void => setMenuOpen(false)

        router.events.on('routeChangeComplete', close)

        return () => router.events.off('routeChangeComplete', close)
    }, [router.events])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setMenuOpen(false)
            }
        }

        const onResize = (): void => {
            if (window.innerWidth > COLLAPSE_WIDTH) {
                setMenuOpen(false)
            }
        }

        document.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [])

    return (
        <div className='header'>
            <div
                role='banner'
                data-collapse='medium'
                className='navbar-section w-nav'
            >
                <div className='nav-container w-container'>
                    <div className='nav-wrapper'>
                        <Link
                            href='/'
                            className={join('nav-logo w-nav-brand', router.pathname === '/' && 'w--current')}
                            aria-label={`${siteConfig.name} home`}
                        >
                            <Logo className='primary-site-logo' />
                        </Link>

                        <div className='nav-right'>
                            <nav
                                id='primary-navigation'
                                className='menu-wrapper w-nav-menu'
                                {...(menuOpen ? { 'data-nav-menu-open': '' } : {})}
                            >
                                <div className='menu-content-wrapper'>
                                    <div className='menu-item-wrapper'>
                                        <div className='menu-items'>
                                            {primaryNav.map((link) => (
                                                <Link
                                                    key={link.href}
                                                    href={link.href}
                                                    className={join('single-nav style-02', isCurrent(link.href) && 'w--current')}
                                                >
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>

                                        <div className='nav-bottom-right'>
                                            <a href={siteConfig.phoneHref} className='text-link'>
                                                {siteConfig.phone}
                                            </a>

                                            <Button href='/book-free-consultation' label='Get Free Estimate' />

                                            <div className='nav-divider' />
                                        </div>
                                    </div>
                                </div>
                            </nav>
                        </div>

                        <button
                            type='button'
                            className={join('nav-trigger w-nav-button', menuOpen && 'w--open')}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            aria-controls='primary-navigation'
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            <img src='/images/menu-01.svg' loading='lazy' alt='' className='menu-icon' />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
